import { get, has, isObject } from 'lodash';
import React, { useRef } from 'react';
import {
  ANY_OF_KEY,
  deepEquals,
  enumOptionsValueForIndex,
  type FieldProps,
  getDiscriminatorFieldFromSchema,
  getTemplate,
  getWidget,
  mergeSchemas,
  ONE_OF_KEY,
  type RJSFSchema,
  titleId,
  type UiSchema,
} from '@rjsf/utils';
import type { JSONSchema7Definition } from 'json-schema';
import type { EnumOptionsType } from '@rjsf/utils/src/types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';

function getMatchingOption(schema: any, schemaUtils: any, selectedOption: number, formData: any | undefined, options: any[]): number {
  const discriminator = getDiscriminatorFieldFromSchema<any>(schema);

  if (!options) {
    return 0;
  }

  return schemaUtils.getClosestMatchingOption(formData, options, selectedOption, discriminator);
}

export function CustomAnyOfField(props: FieldProps) {
  const { schema, uiSchema, errorSchema, title, name, idSchema, formContext, disabled = false, onChange, onBlur } = props;

  const { widgets, fields, schemaUtils } = props.registry;

  const schemaEntries = (schema.oneOf || schema.anyOf) as NonNullable<JSONSchema7Definition>[];
  const { SchemaField: _SchemaField } = fields;
  let optionSchema: any | undefined | null;
  let option: RJSFSchema = {};

  const retrievedOptions: RJSFSchema[] = schemaEntries?.map((_schema: JSONSchema7Definition) =>
    schemaUtils.retrieveSchema(isObject(_schema) ? _schema : {}, props.formData),
  );

  const prevFormData = useRef<any>(props.formData);
  const [selectedOption, setSelectedOption] = React.useState(() => getMatchingOption(schema, schemaUtils, 0, props.formData, retrievedOptions));
  const SelectWidget = getWidget({ type: 'number' }, 'select', widgets);
  const FieldErrorTemplate = getTemplate<'FieldErrorTemplate'>('FieldErrorTemplate', props.registry, {});

  // When the external form data changes, we need to update the selected option
  if (!deepEquals(prevFormData.current, props.formData)) {
    setSelectedOption(getMatchingOption(schema, schemaUtils, 0, props.formData, retrievedOptions));
    prevFormData.current = props.formData;
  }

  const getFieldId = (): string => {
    const { idSchema, schema } = props;
    return `${idSchema.$id}${schema.oneOf ? '__oneof_select' : '__anyof_select'}`;
  };

  const onOptionChange = (option?: string) => {
    const intOption = option !== undefined ? parseInt(option, 10) : -1;
    if (intOption === selectedOption) {
      return;
    }

    const newOption = intOption >= 0 ? retrievedOptions[intOption] : undefined;

    // We don't want to keep any of the old data, so we completely reset the form data.
    let newFormData = {};
    if (newOption) {
      newFormData = schemaUtils.getDefaultFormState(newOption, newFormData, 'excludeObjectChildren');
    }

    setSelectedOption(intOption);

    if (errorSchema) {
      if (!errorSchema.__errors) {
        errorSchema.__errors = [];
      }

      if (props.rawErrors) {
        errorSchema.__errors.push(...props.rawErrors);
      }
    }

    onChange(newFormData, errorSchema, getFieldId());
  };

  const onOptionsBlur = (id: string, value: string): void => {
    onBlur(id, enumOptionsValueForIndex(value, enumOptions, 0));
  };

  if (retrievedOptions) {
    const { required } = schema;

    option = retrievedOptions[selectedOption];
    optionSchema = required ? mergeSchemas({ required }, option) : option;
  } else {
    return null;
  }

  // First we will check to see if there is an anyOf/oneOf override for the UI schema
  let optionsUiSchema: UiSchema[] = [];
  if (ONE_OF_KEY in schema && uiSchema && ONE_OF_KEY in uiSchema) {
    if (Array.isArray(uiSchema[ONE_OF_KEY])) {
      optionsUiSchema = uiSchema[ONE_OF_KEY];
    } else {
      console.warn(`uiSchema.oneOf is not an array for "${title || name}"`);
    }
  } else if (ANY_OF_KEY in schema && uiSchema && ANY_OF_KEY in uiSchema) {
    if (Array.isArray(uiSchema[ANY_OF_KEY])) {
      optionsUiSchema = uiSchema[ANY_OF_KEY];
    } else {
      console.warn(`uiSchema.anyOf is not an array for "${title || name}"`);
    }
  }

  // Then we pick the one that matches the selected option index, if one exists otherwise default to the main uiSchema
  let optionUiSchema = uiSchema;
  if (selectedOption >= 0 && optionsUiSchema.length > selectedOption) {
    optionUiSchema = optionsUiSchema[selectedOption];
  }

  const enumOptions: EnumOptionsType[] = retrievedOptions.map((opt, index: number): EnumOptionsType => {
    return {
      label: get(optionUiSchema, 'ui:title') || opt.title || opt.schema.name,
      value: index,
    };
  });

  const uiSchemaTitle = get(props.uiSchema, 'ui:title');
  const hideUiTitle = has(props.uiSchema, 'ui:title') && props.uiSchema['ui:title'] === false;

  const label = hideUiTitle ? null : uiSchemaTitle || props.schema.title || name;
  const headerTitle = (
    <>
      {label}
      {enumOptions.length === 1 ? (
        <span>
          :<b> {enumOptions[0].label}</b>
        </span>
      ) : (
        ''
      )}
    </>
  );

  const isBorderShadowEnabled = has(props.uiSchema, 'ui:boxed') && props.uiSchema['ui:boxed'] === true;

  const component = (
    <div className="panel panel-default panel-body">
      {label && (
        <Box
          id={titleId(idSchema)}
          mb={1}
          mt={1}
        >
          <Typography variant="h5">{headerTitle}</Typography>
        </Box>
      )}

      {enumOptions.length > 1 && (
        <SelectWidget
          id={getFieldId()}
          name={`${name}${schema.oneOf ? '__oneof_select' : '__anyof_select'}`}
          schema={{ type: 'number', default: 0 }}
          onChange={onOptionChange}
          onBlur={onOptionsBlur}
          onFocus={() => {}}
          multiple={false}
          value={selectedOption >= 0 ? selectedOption : undefined}
          options={{ enumOptions }}
          registry={props.registry}
          formContext={formContext}
          label={title || ''}
          hideLabel={true}
          readonly={disabled}
          rawErrors={props.rawErrors}
        />
      )}

      <FieldErrorTemplate
        errors={props.rawErrors}
        errorSchema={errorSchema}
        idSchema={idSchema}
        schema={schema}
        uiSchema={uiSchema}
        registry={props.registry}
      />

      {optionSchema && optionSchema.type !== 'null' && (
        <_SchemaField
          {...props}
          schema={optionSchema}
          uiSchema={optionUiSchema}
        />
      )}
    </div>
  );

  if (isBorderShadowEnabled) {
    return (
      <Paper elevation={2}>
        <Box p={2}>{component}</Box>
      </Paper>
    );
  } else {
    return component;
  }
}
